import { decodeBase64, encodeBase64 } from './base64';
import { utf8Decode, utf8Encode } from './platform';
import type { MessageReference, ParsedMessageBody } from './types/message';

/**
 * Encoding of reply/forward markers and attachment captions inside a message
 * body.
 *
 * The server stores message bodies as opaque text, so the relationship between
 * two messages has nowhere else to live. The prefix below is byte-for-byte the
 * one the previous interface wrote, which is what lets existing history keep
 * rendering with its replies intact after the migration.
 *
 * `⁣` is an invisible separator: a client that does not understand the
 * prefix shows nothing extra rather than a line of noise.
 */
const META_PREFIX = '⁣LINE_META:';
const ATTACHMENT_PREFIX = '\u{1F4CE} ';
const REFERENCE_KINDS = new Set(['reply', 'forward']);

export function encodeMessageReference(content: string, reference: MessageReference): string {
  const encoded = encodeBase64(utf8Encode(JSON.stringify(reference)));
  return `${META_PREFIX}${encoded}\n${content}`;
}

export function encodeAttachmentBody(fileName: string, caption: string): string {
  return caption ? `${ATTACHMENT_PREFIX}${fileName}\n${caption}` : `${ATTACHMENT_PREFIX}${fileName}`;
}

/**
 * Split a stored body into the parts the interface renders separately.
 *
 * Anything that fails to decode is returned as plain text rather than thrown
 * away: a body that merely looks like a marker is still a message somebody
 * sent, and losing it would be worse than showing the raw prefix.
 */
export function parseMessageBody(rawContent: string | null | undefined): ParsedMessageBody {
  const empty: ParsedMessageBody = {
    content: '',
    reference: null,
    attachmentName: null,
    caption: '',
  };
  if (typeof rawContent !== 'string' || rawContent.length === 0) return empty;

  let content = rawContent;
  let reference: MessageReference | null = null;

  if (content.startsWith(META_PREFIX)) {
    const separator = content.indexOf('\n');
    if (separator > 0) {
      try {
        const decoded = decodeBase64(content.slice(META_PREFIX.length, separator));
        const parsed = JSON.parse(utf8Decode(decoded)) as MessageReference;
        if (parsed && REFERENCE_KINDS.has(parsed.kind)) {
          reference = parsed;
          content = content.slice(separator + 1);
        }
      } catch {
        // Leave the body untouched: better a visible prefix than a lost message.
      }
    }
  }

  if (content.startsWith(ATTACHMENT_PREFIX)) {
    const withoutPrefix = content.slice(ATTACHMENT_PREFIX.length);
    const separator = withoutPrefix.indexOf('\n');
    const attachmentName = separator < 0 ? withoutPrefix : withoutPrefix.slice(0, separator);
    const caption = separator < 0 ? '' : withoutPrefix.slice(separator + 1).trim();
    return { content, reference, attachmentName, caption };
  }

  return { content, reference, attachmentName: null, caption: content };
}

const IMAGE_PATTERN = /\.(avif|gif|jpe?g|png|webp)$/i;
const VIDEO_PATTERN = /\.(mp4|webm|mov)$/i;
const AUDIO_PATTERN = /\.(mp3|wav|ogg|m4a|opus)$/i;

export function attachmentKind(name: string): 'image' | 'video' | 'audio' | 'document' {
  if (IMAGE_PATTERN.test(name)) return 'image';
  if (VIDEO_PATTERN.test(name)) return 'video';
  if (AUDIO_PATTERN.test(name)) return 'audio';
  return 'document';
}

/** Build the short quoted line shown above a reply or forward in the composer. */
export function referenceExcerpt(body: string, limit = 160): string {
  const parsed = parseMessageBody(body);
  const text = parsed.attachmentName
    ? parsed.caption || parsed.attachmentName
    : parsed.content;
  return text.replace(/\s+/g, ' ').trim().slice(0, limit) || 'Вложение';
}
